/**
 * Карточка отсчёта: состояние одного запроса «что в этой точке».
 *
 * Карточка ЗАКРЕПЛЁННАЯ по определению — она держит запрос в сеть, и открывать
 * её по наведению значило бы слать запрос на каждый пиксель. Поэтому здесь нет
 * пары «читалка/закреплённая», как у землетрясения: есть точка или нет её.
 *
 * ЗАПРОС ОТМЕНЯЕТСЯ ПЕРЕКЛЮЧЕНИЕМ ТОЧКИ. Два клика подряд дают две гонки, и
 * без счётчика поколений медленный первый ответ перезаписал бы быстрый второй:
 * карточка стояла бы на новой точке с числами от старой. `AbortController` тут
 * недостаточен — прерванный fetch всё равно доходит до `catch`, а решает не он,
 * а проверка «моё ли это поколение».
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { cabinetApi, type PointProbeResponse } from '../api'

export interface PointPick {
  lng: number
  lat: number
}

export interface PointCardState {
  /** Точка, о которой спрашиваем; null — карточки нет. */
  pick: PointPick | null
  probe: PointProbeResponse | null
  loading: boolean
  /** Запрос не прошёл — говорим об этом, а не показываем пустые строки. */
  failed: boolean
  open: (pick: PointPick) => void
  close: () => void
}

export function usePointCard(params: {
  date: string
  depth: number
  species?: number | null
}): PointCardState {
  const [pick, setPick] = useState<PointPick | null>(null)
  const [probe, setProbe] = useState<PointProbeResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const genRef = useRef(0)

  const { date, depth, species } = params

  useEffect(() => {
    if (!pick) return
    const gen = ++genRef.current
    setLoading(true)
    setFailed(false)
    cabinetApi
      .point({ lat: pick.lat, lon: pick.lng, date, depth, species })
      .then(res => {
        if (gen !== genRef.current) return
        setProbe(res)
        setLoading(false)
      })
      .catch(() => {
        if (gen !== genRef.current) return
        setProbe(null)
        setFailed(true)
        setLoading(false)
      })
    // Дата, глубина и вид — часть ВОПРОСА, а не оформления: сменив горизонт,
    // человек спрашивает про ту же точку заново, и старые числа под курсором
    // стали бы враньём молча.
  }, [pick, date, depth, species])

  const open = useCallback((next: PointPick) => {
    // Прошлые числа снимаем сразу: они относятся к другой точке, и мелькнуть
    // под новыми координатами им нельзя.
    setProbe(null)
    setFailed(false)
    setPick(next)
  }, [])

  const close = useCallback(() => {
    // Поколение сдвигаем и на закрытии: ответ на лету не должен воскресить
    // только что закрытую карточку.
    genRef.current += 1
    setPick(null)
    setProbe(null)
    setLoading(false)
    setFailed(false)
  }, [])

  return { pick, probe, loading, failed, open, close }
}
